import { Box, Button, Stack, Typography } from "@mui/material";
import { useEffect } from "react";
import { Link as RouterLink, isRouteErrorResponse, useRouteError } from "react-router-dom";

import { ROUTES } from "@/constants";
import { useTranslation } from "@/context/I18nProvider";
import { isStaleBuildError, reloadForStaleBuild } from "@/lib/stale-build";

// Replaces React Router's default crash screen when a page throws while rendering.
export default function RouteError() {
  const error = useRouteError();
  const { t } = useTranslation();

  // Strings come from the backend; fall back to English until it ships these keys.
  const tr = (key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const detail = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : "";

  // A page file missing after a deploy: reload into the new build instead of showing the error.
  const staleBuild = isStaleBuildError(error);
  useEffect(() => {
    if (staleBuild && reloadForStaleBuild()) return;
    console.error(error);
  }, [error, staleBuild]);

  return (
    <Box sx={{ display: "flex", justifyContent: "center", p: { xs: 2, sm: 6 } }}>
      <Stack spacing={2} sx={{ maxWidth: 520, alignItems: "center", textAlign: "center" }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {tr("common.pageErrorTitle", "Something went wrong")}
        </Typography>
        <Typography color="text.secondary">
          {tr(
            "common.pageErrorDesc",
            "This page could not be displayed. Reload to try again, or go back to the dashboard.",
          )}
        </Typography>
        {detail && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace", wordBreak: "break-word" }}
          >
            {detail}
          </Typography>
        )}
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={() => window.location.reload()}>
            {tr("common.reload", "Reload")}
          </Button>
          <Button variant="outlined" component={RouterLink} to={ROUTES.HOME}>
            {tr("common.goHome", "Go home")}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
